import React, { useMemo, useState } from 'react';
import { HELP_TOPICS, HELP_CATEGORIES } from '../help/helpTopics';
import './HelpCenter.css';

// Render a topic's markdown-ish body: blank lines split blocks; within a block,
// leading text lines become a paragraph (a single line ending ":" is a sub-head)
// and lines starting "- " become a bullet list.
function renderBody(text) {
  const blocks = (text || '').split(/\n{2,}/);
  return blocks.map((block, bi) => {
    const lines = block.split('\n');
    const lead = [];
    const bullets = [];
    for (const ln of lines) {
      if (ln.trim().startsWith('- ')) bullets.push(ln.trim().slice(2));
      else if (ln.trim()) lead.push(ln.trim());
    }
    return (
      <React.Fragment key={bi}>
        {lead.length > 0 && (
          lead.length === 1 && lead[0].endsWith(':')
            ? <p className="help-subhead">{lead[0]}</p>
            : <p className="help-p">{lead.join(' ')}</p>
        )}
        {bullets.length > 0 && (
          <ul className="help-ul">{bullets.map((b, i) => <li key={i}>{b}</li>)}</ul>
        )}
      </React.Fragment>
    );
  });
}

const HelpCenter = ({ isAdmin = false, onClose }) => {
  const [query, setQuery] = useState('');

  // Visible topics: hide admin-only material from non-admins.
  const topics = useMemo(
    () => HELP_TOPICS.filter(t => isAdmin || !t.adminOnly),
    [isAdmin]
  );

  const q = query.trim().toLowerCase();
  const matches = useMemo(() => {
    if (!q) return topics;
    return topics.filter(t =>
      t.title.toLowerCase().includes(q) ||
      (t.keywords || '').toLowerCase().includes(q) ||
      t.body.toLowerCase().includes(q)
    );
  }, [q, topics]);

  const [activeId, setActiveId] = useState(topics[0]?.id);
  // If the current selection isn't in the filtered set, fall back to the first match.
  const active = matches.find(t => t.id === activeId) || matches[0] || null;

  // Categories that still have at least one matching topic, in canonical order.
  const cats = HELP_CATEGORIES.filter(c => matches.some(t => t.category === c));

  return (
    <div className="help-overlay" onClick={onClose}>
      <div className="help-modal" onClick={(e) => e.stopPropagation()}>
        <div className="help-header">
          <h2>Help {isAdmin && <span className="help-admin-tag">Admin</span>}</h2>
          <button className="help-close" onClick={onClose} aria-label="Close">×</button>
        </div>

        <div className="help-search">
          <input
            type="text"
            placeholder="Search help… (e.g. proposal, retainer, add a user)"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            autoFocus
          />
          {query && <button className="help-search-clear" onClick={() => setQuery('')} aria-label="Clear">×</button>}
        </div>

        <div className="help-body">
          <nav className="help-index">
            {cats.length === 0 && <div className="help-noresults">No topics match “{query}”.</div>}
            {cats.map(cat => (
              <div key={cat} className="help-index-group">
                <div className="help-index-cat">{cat}</div>
                {matches.filter(t => t.category === cat).map(t => (
                  <button
                    key={t.id}
                    className={`help-index-item ${active && active.id === t.id ? 'active' : ''}`}
                    onClick={() => setActiveId(t.id)}
                  >
                    {t.title}
                    {t.adminOnly && <span className="help-lock" title="Admin only">🔒</span>}
                  </button>
                ))}
              </div>
            ))}
          </nav>

          <article className="help-content">
            {active ? (
              <>
                <div className="help-content-cat">{active.category}</div>
                <h3 className="help-content-title">
                  {active.title}
                  {active.adminOnly && <span className="help-admin-tag">Admin</span>}
                </h3>
                <div className="help-content-body">{renderBody(active.body)}</div>
              </>
            ) : (
              <div className="help-noresults">Type to search, or pick a topic from the index.</div>
            )}
          </article>
        </div>
      </div>
    </div>
  );
};

export default HelpCenter;
